// The worker's glove card on the QEW Dashboard. Pure: pair in, card out.
// Words: GLOVES_REWORK_WORDS.md (approved 2026-10-06). Mockup: MOCKUP_GLOVES_REWORK.html.
// Shared by the Dashboard, the glove server steps and the nightly expiry job, and unit tested.
// Nothing here reads or writes the database.
//
// Rules (Robert, 2026-10-06):
// * The app runs the replacement on the clocks. The QEW only: picks his size once,
//   records a pair when it arrives, taps Place In Service (no JSP), removes for Damage or Lost.
// * A sealed bag means a test date only. An opened bag means the pair is in service, with a date.
// * Clocks (OSHA 1910.137 Table I-5, ASTM F496): sealed, 12 months from the test date.
//   In service, 6 months from the service date, nothing else. Amber 30 days out.
// * A nightly job retires expired pairs. The QEW never decides a pair is done.
// * Nothing stops work. Every state is information only.
// * One pair at a time. Pair ID is the tag plus a number: 01-QEW04-P1, never reused.

export const GLOVE_SIZES = ["7", "8", "8.5", "9", "9.5", "10", "10.5", "11", "11.5", "12"] as const;
export const GLOVE_CLASS = "Class 0";
export const GLOVE_CLASS_LINE = "Class 0 · 1000 V · set by program";
export const SHELF_MONTHS = 12;
export const SERVICE_MONTHS = 6;
export const GLOVE_WARNING_DAYS = 30;
/** What the QEW can pick on Remove From Service. Expiry is the app's job. */
export const REMOVAL_REASONS = ["Damage", "Lost"] as const;
export type RemovalReason = (typeof REMOVAL_REASONS)[number];
/** Every reason a pair can retire. The last three are stamped by the app. */
export type RetireReason = RemovalReason | "Expired Discarded" | "Expired In Bag" | "Replaced";
/** removedBy on a pair the app retired. */
export const APP_STAMP = "SafeTag";

export type GloveStatusWord = "sealed" | "inService" | "retired";

/** A pair as stored at gloves/{pairId}. */
export interface GlovePair {
  pairId: string;
  qewId: string;
  gloveClass: string;
  gloveSize: string;
  manufacturer: string | null;
  testedOn: string;
  status: GloveStatusWord;
  inServiceOn: string | null;
  inServiceJspNumber: string | null;
}

/** The last pair the app retired, for the red line on Waiting On Gloves. */
export interface RetiredNote {
  pairId: string;
  removedOn: string;
  removalReason: string;
}

export interface LastInspection {
  inspectedOn: string;
  jspNumber: string | null;
}

/** Everything the glove card reads. */
export interface GloveView {
  gloveSize: string | null;
  pair: GlovePair | null;
  lastInspection: LastInspection | null;
  lastRetired: RetiredNote | null;
}

/** Older records said awaitingService. They read as sealed. */
export function statusOf(raw: unknown): GloveStatusWord {
  if (raw === "inService") return "inService";
  if (raw === "retired") return "retired";
  return "sealed";
}

/** A stored pair as the rules read it. */
export function pairOf(id: string, d: Record<string, unknown>): GlovePair {
  const s = (v: unknown) => (typeof v === "string" && v ? v : null);
  return {
    pairId: id,
    qewId: String(d.qewId ?? ""),
    gloveClass: String(d.gloveClass ?? "Class 0"),
    gloveSize: String(d.gloveSize ?? ""),
    manufacturer: s(d.manufacturer),
    testedOn: String(d.testedOn ?? ""),
    status: statusOf(d.status),
    inServiceOn: s(d.inServiceOn),
    inServiceJspNumber: s(d.inServiceJspNumber),
  };
}

// ---- Dates ----

/** YYYY-MM-DD plus whole months. 31 March plus 6 lands on 30 September. */
export function addMonths(iso: string, months: number): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  const total = y * 12 + (m - 1) + months;
  const ty = Math.floor(total / 12);
  const tm = (total % 12) + 1;
  const last = new Date(Date.UTC(ty, tm, 0)).getUTCDate();
  return `${String(ty).padStart(4, "0")}-${String(tm).padStart(2, "0")}-${String(Math.min(d, last)).padStart(2, "0")}`;
}

function isIsoDate(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

function days(fromIso: string, toIso: string): number {
  return Math.round((Date.parse(`${toIso}T00:00:00Z`) - Date.parse(`${fromIso}T00:00:00Z`)) / 86_400_000);
}

export function usDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${m}/${d}/${y}` : iso;
}

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;

/** The last good day of a pair: use by (sealed) or replace by (in service). */
export function dueOf(pair: Pick<GlovePair, "testedOn" | "status" | "inServiceOn">): string {
  return pair.status === "inService" && pair.inServiceOn ? addMonths(pair.inServiceOn, SERVICE_MONTHS) : addMonths(pair.testedOn, SHELF_MONTHS);
}

/** Inside 30 days of the due date, or past it. A replacement can be recorded. */
export function canReplace(pair: GlovePair, today: string): boolean {
  return days(today, dueOf(pair)) <= GLOVE_WARNING_DAYS;
}

/** The nightly job's answer for one pair: retire it, and why. Null keeps it. */
export function expiryOf(pair: GlovePair, today: string): RetireReason | null {
  if (pair.status === "retired") return null;
  if (dueOf(pair) >= today) return null;
  return pair.status === "inService" ? "Expired Discarded" : "Expired In Bag";
}

// ---- The card ----

export type GloveState = "pickSize" | "waiting" | "sealed" | "sealedSoon" | "inService" | "inServiceSoon" | "expired";
export type GloveAction = "saveSize" | "arrived" | "placeInService";

export interface GloveCard {
  state: GloveState;
  tone: "wait" | "ok" | "warn" | "bad";
  title: string;
  rows: { label: string; value: string; tone?: "ok" | "warn" | "bad"; mono?: boolean }[];
  /** Red line naming a pair the app retired. Waiting On Gloves only. */
  retiredLine: string | null;
  note: { tone: "plain" | "amber" | "red"; text: string } | null;
  /** Main buttons, in order. One in most states. */
  actions: GloveAction[];
  showChangeSize: boolean;
  showRemove: boolean;
  /** The line under "PPE and gloves" on the Dashboard. */
  dashLine: string;
  dashTone: "ok" | "warn" | "bad" | "wait";
}

export function gloveCardOf(v: GloveView, today: string): GloveCard {
  const pair = v.pair && v.pair.status !== "retired" ? v.pair : null;
  if (!pair) {
    if (!v.gloveSize) {
      return {
        state: "pickSize",
        tone: "wait",
        title: "Pick Your Size",
        rows: [],
        retiredLine: null,
        note: { tone: "plain", text: "Training is done. Pick your glove size so your gloves can be ordered." },
        actions: ["saveSize"],
        showChangeSize: false,
        showRemove: false,
        dashLine: "Pick your glove size",
        dashTone: "wait",
      };
    }
    const r = v.lastRetired;
    const byApp = r && (r.removalReason === "Expired Discarded" || r.removalReason === "Expired In Bag");
    return {
      state: "waiting",
      tone: "wait",
      title: "Waiting On Gloves",
      rows: [
        { label: "Size", value: v.gloveSize },
        { label: "Class", value: "Class 0 · 1000 V" },
      ],
      retiredLine: byApp
        ? `Your ${r.removalReason === "Expired In Bag" ? "sealed pair" : "pair"} ${r.pairId} expired${r.removalReason === "Expired In Bag" ? " in the bag" : ""} on ${usDate(r.removedOn)}. Discard it. Do not use it.`
        : null,
      note: { tone: "plain", text: "When your gloves arrive, tap Gloves Arrived. Keep the bag sealed until you need them." },
      actions: ["arrived"],
      showChangeSize: true,
      showRemove: false,
      dashLine: byApp ? "Pair expired · waiting on gloves" : `Waiting on gloves · Size ${v.gloveSize}`,
      dashTone: byApp ? "bad" : "wait",
    };
  }

  const due = dueOf(pair);
  const left = days(today, due);
  const pairRow = { label: "Pair", value: pair.pairId, mono: true };
  const classRow = { label: "Class · size", value: `${pair.gloveClass} · ${pair.gloveSize}` };
  const testRow = { label: "Test date", value: usDate(pair.testedOn) };
  const replaceLine = `Replacement needed by ${usDate(due)}.`;

  if (left < 0) {
    // Only between midnight runs, or if the nightly job missed. The job retires it.
    return {
      state: "expired",
      tone: "bad",
      title: "Expired",
      rows: [pairRow, { label: "Was good until", value: `${usDate(due)} · ${plural(-left, "day")} over`, tone: "bad" }],
      retiredLine: null,
      note: { tone: "red", text: "Replace this pair. Do not use it." },
      actions: ["arrived"],
      showChangeSize: false,
      showRemove: true,
      dashLine: "Pair expired · replace it",
      dashTone: "bad",
    };
  }

  const soon = left <= GLOVE_WARNING_DAYS;
  if (pair.status !== "inService") {
    return {
      state: soon ? "sealedSoon" : "sealed",
      tone: soon ? "warn" : "wait",
      title: soon ? `Sealed · ${plural(left, "Day")} Left` : "Sealed In Bag",
      rows: [pairRow, classRow, testRow, { label: "Use by", value: usDate(due), tone: soon ? "warn" : undefined }],
      retiredLine: null,
      note: soon
        ? { tone: "amber", text: `${replaceLine} Place this pair in service now, or record a new pair when it arrives.` }
        : { tone: "plain", text: "Open the bag the first time you need them, then tap Place In Service. A JSP does it for you." },
      actions: soon ? ["placeInService", "arrived"] : ["placeInService"],
      showChangeSize: false,
      showRemove: true,
      dashLine: soon ? `Replacement needed by ${usDate(due)}` : `Sealed in bag · ${pair.pairId}`,
      dashTone: soon ? "warn" : "wait",
    };
  }

  const since = `${usDate(pair.inServiceOn!)}${pair.inServiceJspNumber ? ` · ${pair.inServiceJspNumber}` : ""}`;
  const last = v.lastInspection;
  return {
    state: soon ? "inServiceSoon" : "inService",
    tone: soon ? "warn" : "ok",
    title: `In Service · ${plural(left, "Day")} Left`,
    rows: [
      pairRow,
      classRow,
      testRow,
      { label: "In service since", value: since },
      { label: "Replace by", value: usDate(due), tone: soon ? "warn" : "ok" },
      ...(last ? [{ label: "Last inspected", value: `${usDate(last.inspectedOn)}${last.jspNumber ? ` · ${last.jspNumber}` : ""}` }] : []),
    ],
    retiredLine: null,
    note: soon
      ? { tone: "amber", text: `${replaceLine} Get a sealed pair. When it is in hand, tap Gloves Arrived.` }
      : { tone: "plain", text: "Inspect before every use: visual and air test, both hands." },
    actions: soon ? ["arrived"] : [],
    showChangeSize: false,
    showRemove: true,
    dashLine: soon ? `Replacement needed by ${usDate(due)}` : `In service · ${plural(left, "day")} left`,
    dashTone: soon ? "warn" : "ok",
  };
}

// ---- Gloves Arrived, guided ----

export interface ArrivalInput {
  /** Answer to step 1. Null until he picks. */
  sealed: boolean | null;
  testedOn: string;
  /** Opened only: "today", or a YYYY-MM-DD for Earlier. */
  firstUse: string;
}

export interface ArrivalProblem {
  field: "sealed" | "testedOn" | "firstUse";
  message: string;
}

/** The test date check alone. Step 3 appears only when this passes. */
export function testDateProblem(testedOn: string, today: string): string | null {
  const t = testedOn.trim();
  if (!isIsoDate(t)) return "Enter the test date.";
  if (t > today) return "That test date is in the future. Check the date.";
  if (addMonths(t, SHELF_MONTHS) < today) return "That test date is over 12 months old. This pair needs replacing.";
  return null;
}

/** The first use date as a date, today filled in for "today". */
export function firstUseOn(input: ArrivalInput, today: string): string {
  return input.firstUse === "today" ? today : input.firstUse.trim();
}

/** Every check on Gloves Arrived. Null when it can be saved. */
export function arrivalProblem(input: ArrivalInput, today: string): ArrivalProblem | null {
  if (input.sealed === null) return { field: "sealed", message: "Are the gloves still sealed in the manufacturer's bag?" };
  const t = testDateProblem(input.testedOn, today);
  if (t) return { field: "testedOn", message: t };
  if (input.sealed) return null;
  const used = firstUseOn(input, today);
  if (!isIsoDate(used)) return { field: "firstUse", message: "When did you first use them?" };
  if (used > today) return { field: "firstUse", message: "That date is in the future. Check the date." };
  if (used < input.testedOn) return { field: "firstUse", message: "First use can't be before the test date." };
  if (used > addMonths(input.testedOn, SHELF_MONTHS))
    return { field: "firstUse", message: "These gloves were first used more than 12 months after their test date. This pair needs replacing." };
  if (addMonths(used, SERVICE_MONTHS) < today) return { field: "firstUse", message: "This pair is past 6 months in service. Replace it." };
  return null;
}

/** The next pair ID for a QEW: his tag plus the next number. IDs are never reused. */
export function nextPairId(qewId: string, existing: readonly string[]): string {
  let n = 0;
  for (const id of existing) {
    const m = id.match(/-P(\d+)$/);
    if (m && id.startsWith(`${qewId}-P`)) n = Math.max(n, Number(m[1]));
  }
  return `${qewId}-P${n + 1}`;
}

type Who = { badgeId: string; holderName: string };

/** The record for a pair that just arrived. Sealed, or in service from the date he gave. */
export function newPairRecord(
  pairId: string,
  qewId: string,
  input: ArrivalInput & { gloveSize: string; manufacturer: string },
  who: Who,
  today: string,
  nowIso: string,
): Record<string, unknown> {
  const sealed = input.sealed === true;
  const used = sealed ? null : firstUseOn(input, today);
  return {
    pairId,
    qewId,
    siteNumber: qewId.slice(0, 2),
    gloveClass: GLOVE_CLASS,
    gloveSize: input.gloveSize,
    manufacturer: input.manufacturer.trim() || null,
    testedOn: input.testedOn,
    arrivedSealed: sealed,
    status: sealed ? "sealed" : "inService",
    issuedOn: today,
    inServiceOn: used,
    inServiceBy: sealed ? null : who.badgeId,
    inServiceByName: sealed ? null : who.holderName,
    inServiceHow: sealed ? null : "Reported opened",
    inServiceJspNumber: null,
    removedOn: null,
    removalReason: null,
    notes: null,
    addedBy: who.badgeId,
    addedByName: who.holderName,
    createdAt: nowIso,
  };
}

/** One tap: the sealed pair goes in service today. The JSP passes its number. */
export function inServiceFields(who: Who, today: string, nowIso: string, jspNumber: string | null = null) {
  return {
    status: "inService" as const,
    inServiceOn: today,
    inServiceBy: who.badgeId,
    inServiceByName: who.holderName,
    inServiceHow: jspNumber ? "JSP" : "One tap",
    inServiceJspNumber: jspNumber,
    inServiceAt: nowIso,
  };
}

/** Why Place In Service is refused. Null when it can go. */
export function placeInServiceProblem(pair: GlovePair | null, today: string): string | null {
  if (!pair || pair.status === "retired") return "No pair on file.";
  if (pair.status === "inService") return `${pair.pairId} is already in service.`;
  if (dueOf(pair) < today) return `${pair.pairId} expired in the bag. Do not use it.`;
  return null;
}

/** The fields that retire a pair. The app stamps itself on expiry and Replaced. */
export function retireFields(reason: RetireReason, notes: string, by: Who | typeof APP_STAMP, today: string, nowIso: string) {
  const app = by === APP_STAMP;
  return {
    status: "retired" as const,
    removedOn: today,
    removalReason: reason,
    notes: notes.trim() || null,
    removedBy: app ? APP_STAMP : by.badgeId,
    removedByName: app ? APP_STAMP : by.holderName,
    removedAt: nowIso,
  };
}

// ---- The JSP glove inspection (Stage 4 item 4, JSP_WRITES_WORDS.md, approved 2026-10-09) ----
// The JSP asks Pass or Fail on the PPE node. On a Fail he picks what he found.
// Submit writes one gloveInspections record and acts on the pair. Nothing stops work.

/** What Did You Find? Common rubber glove defects (ASTM F1236), most likely first. Robert, 2026-10-09. */
export const GLOVE_DEFECTS = [
  "Failed air test (leak)",
  "Hole, cut or puncture",
  "Tear or split",
  "Cracks or ozone cutting",
  "Swollen, soft or sticky",
  "Hard or brittle",
  "Object stuck in the rubber",
  "Other",
] as const;
export type GloveDefect = (typeof GLOVE_DEFECTS)[number];

/** Why a Fail is not finished yet. Null when the answer can be saved. */
export function gloveFailProblem(defect: string | null, note: string): string | null {
  if (!defect || !(GLOVE_DEFECTS as readonly string[]).includes(defect)) return "Pick what you found on the gloves.";
  if (defect === "Other" && !note.trim()) return "Tell us what you found on the gloves.";
  return null;
}

/** What Submit does with the pair on his card. */
export interface InspectionPlan {
  /** Blank when no usable pair is on file (none, retired, or expired). */
  pairId: string;
  inspectionType: "firstUse" | "beforeUse";
  /** A Pass on a sealed pair in date. */
  putInService: boolean;
  /** A Fail on a pair on file. */
  retire: boolean;
}

/**
 * Pass, sealed and in date: first use, goes in service. Pass, in service and in date: before use.
 * Fail on a pair on file: retires as Damage. No pair, or an expired one: logged with no pair.
 */
export function inspectionPlanOf(pair: GlovePair | null, passed: boolean, today: string): InspectionPlan {
  const live = pair && pair.status !== "retired" ? pair : null;
  if (!live) return { pairId: "", inspectionType: "beforeUse", putInService: false, retire: false };
  const expired = dueOf(live) < today;
  if (!passed) return { pairId: live.pairId, inspectionType: live.status === "inService" ? "beforeUse" : "firstUse", putInService: false, retire: true };
  if (expired) return { pairId: "", inspectionType: "beforeUse", putInService: false, retire: false };
  return live.status === "inService"
    ? { pairId: live.pairId, inspectionType: "beforeUse", putInService: false, retire: false }
    : { pairId: live.pairId, inspectionType: "firstUse", putInService: true, retire: false };
}

/** A glove or inspection document as read, for the view below. */
export interface GloveDocIn {
  id: string;
  data: Record<string, unknown>;
}

/**
 * Everything the glove card reads, from his pairs and inspections. Shared by the Dashboard
 * and the JSP pill, so the two never disagree.
 */
export function gloveViewOf(gloveSize: string | null, pairs: readonly GloveDocIn[], inspections: readonly GloveDocIn[]): GloveView {
  const str = (v: unknown) => (typeof v === "string" && v ? v : null);
  const active = pairs.find((d) => d.data.status !== "retired");
  const pair = active ? pairOf(active.id, active.data) : null;
  const lastRetired: RetiredNote | null = pair
    ? null
    : (pairs
        .filter((d) => d.data.status === "retired" && d.data.removedBy === APP_STAMP && String(d.data.removalReason ?? "").startsWith("Expired"))
        .map((d) => ({ pairId: d.id, removedOn: String(d.data.expiredOn ?? d.data.removedOn ?? ""), removalReason: String(d.data.removalReason), at: String(d.data.removedAt ?? "") }))
        .sort((a, b) => a.at.localeCompare(b.at))
        .map(({ at: _at, ...r }) => r)
        .pop() ?? null);
  const lastInspection = pair
    ? (inspections
        .filter((d) => d.data.pairId === pair.pairId)
        .map((d) => ({ inspectedOn: str(d.data.inspectedOn) ?? String(d.data.inspectedAt ?? "").slice(0, 10), jspNumber: str(d.data.jspNumber), at: String(d.data.inspectedAt ?? "") }))
        .filter((x) => x.inspectedOn)
        .sort((a, b) => a.inspectedOn.localeCompare(b.inspectedOn) || a.at.localeCompare(b.at))
        .map(({ at: _at, ...x }) => x)
        .pop() ?? null)
    : null;
  return { gloveSize: gloveSize || null, pair, lastInspection, lastRetired };
}
